/** Pure API/view adapters. Do not mutate RTK Query's cached objects. */
export function idOf(value) {
 if (typeof value === 'string') return value;
 if (typeof value === 'number') return String(value);
 if (value && typeof value === 'object') return idOf(value._id ?? value.id ?? value.userId);
 return '';
}

/** Support both the controller envelope and an RTK transformResponse result. */
export function campaignFromResponse(value, expectedId) {
 const campaign = value?.campaign ?? value?.data?.campaign ?? value;
 if (!campaign || typeof campaign !== 'object' || !idOf(campaign)) return null;
 if (expectedId && idOf(campaign) !== String(expectedId)) return null;
 return campaign;
}
export function listFromResponse(value) {
 const body = value?.data?.campaigns ? value.data : value;
 const rows = Array.isArray(body?.campaigns) ? body.campaigns : Array.isArray(body) ? body : [];
 return { rows: rows.filter((row) => row && idOf(row)), total: positiveNumber(body?.total ?? rows.length) };
}
export function positiveNumber(value) {
 const n = Number(value);
 return Number.isFinite(n) && n > 0 ? n : 0;
}
export const count = (value) => positiveNumber(value).toLocaleString();
export const stateOf = (campaign) => String(campaign?.state ?? '').toUpperCase();
export function displayName(person) {
 if (!person || typeof person !== 'object') return '';
 for (const value of [person.displayName, person.fullName, [person.firstName, person.lastName].filter(Boolean).join(' '), person.name]) {
  if (typeof value === 'string' && value.trim()) return value.trim();
 }
 return '';
}
export function creatorOf(campaign) {
 const id = idOf(campaign?.createdBy);
 const person = campaign?.createdByUser ?? campaign?.peopleById?.[id] ?? campaign?.createdBy;
 return { id, name: displayName(person) || 'Name unavailable' };
}
export function actorName(value, people = {}) {
 if (!value) return 'Automatic';
 return displayName(people[idOf(value)] ?? value) || 'Name unavailable';
}
export function formatDate(value, includeTime = false) {
 if (!value) return 'Not recorded';
 // Calendar dates are not UTC instants: never shift 2026-10-01 to the previous day.
 const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
 const d = new Date(dateOnly ? `${value}T12:00:00` : value);
 if (!Number.isFinite(d.getTime())) return 'Not recorded';
 return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric',
  ...(includeTime && !dateOnly ? { hour: '2-digit', minute: '2-digit' } : {}) });
}
export function versionTime(campaign) {
 const history = Array.isArray(campaign?.stateHistory) ? campaign.stateHistory : [];
 return Math.max(0, ...[campaign?.updatedAt, ...history.map((e) => e.at)].map((s) => new Date(s ?? 0).getTime() || 0));
}
/** A POST acknowledgement must not be overwritten by an older GET response. */
export function mergeAcknowledgement(server, acknowledged) {
 if (!acknowledged) return server;
 if (!server || idOf(server) !== idOf(acknowledged)) return acknowledged;
 const serverTime = versionTime(server); const ackTime = versionTime(acknowledged);
 if (serverTime && ackTime && serverTime < ackTime) return { ...server, ...acknowledged };
 if (serverTime > ackTime || server.state === acknowledged.state) return { ...acknowledged, ...server };
 // The launch patch never moves PLANNING back to SCHEDULED. Protect older/missing timestamps.
 if (acknowledged.state === 'PLANNING' && server.state === 'SCHEDULED') return { ...server, ...acknowledged };
 return { ...acknowledged, ...server };
}
export function deliveryProgress(campaign) {
 const c = campaign?.counters ?? {};
 const total = positiveNumber(c.scheduled) || positiveNumber(c.sendable);
 const submitted = positiveNumber(c.submitted); const failed = positiveNumber(c.failed);
 // Provider delivery failures can overlap submitted; never add those counters.
 return { total, submitted, failed, delivered: positiveNumber(c.delivered), replied: positiveNumber(c.replied),
  percent: total ? Math.min(100, Math.round((submitted / total) * 100)) : 0 };
}
export function planningSince(campaign) {
 const history = Array.isArray(campaign?.stateHistory) ? campaign.stateHistory : [];
 const entered = [...history].reverse().find((event) => event.to === 'PLANNING');
 const value = entered?.at ?? campaign?.updatedAt;
 const ms = new Date(value ?? '').getTime();
 return Number.isFinite(ms) ? ms : null;
}
export function pageSummary(rows = []) {
 const list = Array.isArray(rows) ? rows : [];
 return { active: list.filter((r) => stateOf(r) === 'RUNNING').length,
  preparing: list.filter((r) => stateOf(r) === 'PLANNING').length,
  ready: list.filter((r) => stateOf(r) === 'SCHEDULED').length,
  attention: list.filter((r) => ['PAUSED', 'FAILED_TO_PLAN', 'PENDING_APPROVAL'].includes(stateOf(r))).length };
}
